import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import styles from './price-info.module.scss.js';
import { clsx } from '../../external/clsx/dist/clsx.js';
import { useScreenClass, FlexBox, ErrorBoundary, ScreenClassProvider } from '@vp/swan';
import { PriceDisplayMode } from '../../types/prices.types.js';
import { PriceInternal } from '../price/price.component.js';
import { UnitPriceWithQuantityInternal } from '../unit-price-with-quantity/unit-price-with-quantity.component.js';
import { VatMessageInternal } from '../vat-message/vat-message.component.js';
import { AlignVAT } from '../../types/vat.types.js';
import { UnitPriceInternal } from '../unit-price/unit-price.component.js';
import { UnitsCountInternal } from '../units-count/units-count.component.js';
import { getPrice } from '../../utils/price.util.js';
import { useLocalization } from '../../hooks/useLocalizations.js';
import { VolumeDiscountInternal } from '../volume-discount/volume-discount.component.js';
import { SetupFeeAndVatInternal } from '../setup-fee-and-vat/setup-fee-and-vat.component.js';

const PriceInfoInternal = forwardRef((props, ref) => {
  const { className, priceInfoConfig, alignVAT = AlignVAT.BOTTOM, isTile = false, placeholdertext, ...rest } = props;
  const classNames = clsx(className, styles.priceInfo);
  const {
    listPrice,
    discountedListPrice,
    unitListPrice,
    unitDiscountedPrice,
    quantity,
    vatInc = false,
    locale,
    shippingIncl = false,
    hideShippingMessageInVatMessage,
    currency,
    minimumFractionDigits,
    unitPriceForMoq,
    basePrice,
    discountedBasePrice,
    setUpFee,
    discountedSetUpFee,
    showSetUpFeeAndVat = false,
    showPriceSummary = false,
    priceDisplayMode
  } = priceInfoConfig;
  const { t } = useLocalization(locale);
  const screenClass = useScreenClass();
  const showEachPrice = priceDisplayMode === PriceDisplayMode.UNIT;
  const renderPrice = (isBold) => {
    let priceConfig = priceInfoConfig;
    const shouldShowEachPrice = showEachPrice && unitListPrice && unitDiscountedPrice;
    if (shouldShowEachPrice) {
      priceConfig = {
        ...priceInfoConfig,
        discountedListPrice: unitDiscountedPrice,
        listPrice: unitListPrice
      };
      return t("unitPrice", {
        pricePerUnit: /* @__PURE__ */ jsx(
          PriceInternal,
          {
            priceConfig,
            placeholdertext,
            fontSkin: isTile ? "body-small" : "title-subsection",
            fontWeight: isBold ? "bold" : "normal"
          }
        )
      });
    }
    return /* @__PURE__ */ jsx(PriceInternal, { priceConfig, placeholdertext, fontSkin: isTile ? "body-small" : "title-subsection", fontWeight: isBold ? "bold" : "normal" });
  };
  const renderVatMessage = () => /* @__PURE__ */ jsx(
    VatMessageInternal,
    {
      locale,
      vatAndShippingConfig: { vatInc, shippingIncl, hideShippingMessageInVatMessage },
      placeholdertext,
      fontSkin: isTile ? "footnote" : "body-small"
    }
  );
  const renderSetupFeeAndVat = () => {
    return /* @__PURE__ */ jsx(
      SetupFeeAndVatInternal,
      {
        setupFee: setUpFee,
        discountedSetupFee: discountedSetUpFee,
        hideShippingMessageInVatMessage,
        placeholdertext,
        vatInc,
        locale,
        currency,
        minimumFractionDigits,
        shippingIncl
      }
    );
  };
  const renderVolumeDiscount = () => {
    if (unitPriceForMoq && unitDiscountedPrice) {
      return /* @__PURE__ */ jsx(
        VolumeDiscountInternal,
        {
          unitPriceForMoq,
          unitDiscountedPrice,
          placeholdertext,
          vatInc,
          locale,
          totalDiscountedPrice: discountedListPrice,
          totalListPrice: listPrice
        }
      );
    }
  };
  const renderUnitPriceAndQuantity = () => {
    if (unitListPrice && unitDiscountedPrice && quantity && !showEachPrice) {
      return /* @__PURE__ */ jsx(
        UnitPriceWithQuantityInternal,
        {
          unitPriceConfig: { ...priceInfoConfig, unitListPrice, unitDiscountedPrice, quantity },
          placeholdertext,
          fontSkin: isTile ? "footnote" : "body-small"
        }
      );
    }
    if (unitListPrice && unitDiscountedPrice && !showEachPrice) {
      return /* @__PURE__ */ jsx(
        UnitPriceInternal,
        {
          unitPriceConfig: { ...priceInfoConfig, unitListPrice, unitDiscountedPrice },
          placeholdertext,
          fontSkin: isTile ? "footnote" : "body-small"
        }
      );
    }
    if (quantity) {
      return /* @__PURE__ */ jsx(UnitsCountInternal, { locale, quantity, placeholdertext, fontSkin: isTile ? "footnote" : "body-small" });
    }
    return null;
  };
  const renderUnitPriceAndQuantityWithPriceSummary = () => {
    return quantity && /* @__PURE__ */ jsx(
      UnitPriceWithQuantityInternal,
      {
        unitPriceConfig: {
          ...priceInfoConfig,
          unitListPrice,
          unitDiscountedPrice,
          quantity,
          basePrice,
          discountedBasePrice,
          setUpFee,
          discountedSetUpFee,
          shippingIncl,
          vatInc,
          locale,
          currency,
          minimumFractionDigits,
          showPriceSummary,
          totalListPrice: listPrice,
          totalDiscountedPrice: discountedListPrice
        },
        placeholdertext,
        fontSkin: isTile ? "footnote" : "body-small"
      }
    );
  };
  const isDiscountedPriceBetter = getPrice(listPrice, vatInc) > getPrice(discountedListPrice, vatInc);
  const renderPriceInfoWithVatAlignedRight = () => /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(FlexBox, { gap: 2, alignItems: "center", children: [
      renderPrice(!isDiscountedPriceBetter),
      renderVatMessage()
    ] }),
    renderUnitPriceAndQuantity()
  ] });
  const renderPriceInfoWithVatAlignedBottom = () => /* @__PURE__ */ jsxs(Fragment, { children: [
    renderPrice(!isDiscountedPriceBetter),
    setUpFee ? /* @__PURE__ */ jsxs(Fragment, { children: [
      showSetUpFeeAndVat ? renderSetupFeeAndVat() : renderVatMessage(),
      showPriceSummary ? renderUnitPriceAndQuantityWithPriceSummary() : renderUnitPriceAndQuantity()
    ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
      showPriceSummary ? renderUnitPriceAndQuantityWithPriceSummary() : renderUnitPriceAndQuantity(),
      showSetUpFeeAndVat ? renderSetupFeeAndVat() : renderVatMessage()
    ] }),
    unitPriceForMoq && renderVolumeDiscount()
  ] });
  return /* @__PURE__ */ jsx(FlexBox, { flexDirection: "column", gap: 1, className: classNames, ...rest, ref, children: alignVAT === AlignVAT.RIGHT && screenClass !== "xs" ? renderPriceInfoWithVatAlignedRight() : renderPriceInfoWithVatAlignedBottom() });
});
const PriceInfo = forwardRef((props, ref) => /* @__PURE__ */ jsx(ErrorBoundary, { fallback: /* @__PURE__ */ jsx(FlexBox, { fontSkin: "title-subsection", children: props.placeholdertext }), children: /* @__PURE__ */ jsx(ScreenClassProvider, { children: /* @__PURE__ */ jsx(PriceInfoInternal, { ...props, ref }) }) }));

export { PriceInfo };
